import React, { useCallback, useEffect, useState } from 'react'
import {
	Button,
	Card,
	DatePicker,
	Form,
	Input,
	Modal,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import {
	CalendarOutlined,
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	GlobalOutlined,
	PlusOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { leaveService } from '../../common/service'

interface Holiday {
	date: string
	name: string
	/** Localized names keyed by BCP 47 locale code, e.g. { km: '...' } */
	translations?: Record<string, string>
	variable?: boolean
}

const CURRENT_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 2 + i)

// Official Cambodia public holidays with EN + KH names.
// Variable (lunar) dates are approximate — confirm annually by Royal Decree.
const getCambodiaHolidays = (year: number): Holiday[] => [
	// ── Fixed holidays ────────────────────────────────────────────────────
	{
		date: `${year}-01-01`,
		name: 'International New Year Day',
		translations: { km: 'ទិវាចូលឆ្នាំសកល' },
	},
	{
		date: `${year}-01-07`,
		name: 'Victory Over Genocide Day',
		translations: { km: 'ទិវាជ័យជម្នះ' },
	},
	{
		date: `${year}-03-08`,
		name: "International Women's Day",
		translations: { km: 'ទិវាអន្តរជាតិស្ត្រី' },
	},
	{
		date: `${year}-05-01`,
		name: 'International Labour Day',
		translations: { km: 'ទិវាពលករអន្តរជាតិ' },
	},
	{
		date: `${year}-05-14`,
		name: "King Norodom Sihamoni's Birthday",
		translations: { km: 'ថ្ងៃព្រះរាជ្ញតិដ្ឋានករណ៍ ព្រះបាទ នរោត្តម សីហមុនី' },
	},
	{
		date: `${year}-06-18`,
		name: "Queen Mother Norodom Monineath's Birthday",
		translations: { km: 'ថ្ងៃព្រះរាជ្ញតិដ្ឋានករណ៍ ព្រះរាជ្ញី​ព្រះ​ម្ដាយ' },
	},
	{ date: `${year}-09-24`, name: 'Constitution Day', translations: { km: 'ទិវារដ្ឋធម្មនុញ្ញ' } },
	{
		date: `${year}-10-23`,
		name: 'Paris Peace Agreement Day',
		translations: { km: 'ទិវាសន្ធិសញ្ញាទីក្រុងប៉ារីស' },
	},
	{
		date: `${year}-10-29`,
		name: 'Norodom Sihamoni Coronation Day',
		translations: { km: 'ទិវាគ្រងរាជ្យសម្បត្តិ ព្រះបាទ នរោត្តម សីហមុនី' },
	},
	{ date: `${year}-11-09`, name: 'Independence Day', translations: { km: 'ទិវាឯករាជ្យជាតិ' } },
	{
		date: `${year}-12-10`,
		name: 'International Human Rights Day',
		translations: { km: 'ទិវាសិទ្ធិមនុស្ស' },
	},
	// ── Variable / lunar holidays (approximate — must be confirmed each year) ─
	{
		date: `${year}-04-13`,
		name: 'Khmer New Year (Day 1)',
		translations: { km: 'ចូលឆ្នាំខ្មែរ (ថ្ងៃទី១)' },
		variable: true,
	},
	{
		date: `${year}-04-14`,
		name: 'Khmer New Year (Day 2)',
		translations: { km: 'ចូលឆ្នាំខ្មែរ (ថ្ងៃទី២)' },
		variable: true,
	},
	{
		date: `${year}-04-15`,
		name: 'Khmer New Year (Day 3)',
		translations: { km: 'ចូលឆ្នាំខ្មែរ (ថ្ងៃទី៣)' },
		variable: true,
	},
	{
		date: `${year}-05-05`,
		name: 'Royal Ploughing Ceremony',
		translations: { km: 'ព្រះរាជពិធីបុណ្យច្រត់ព្រះនង្គ័ល' },
		variable: true,
	},
	{
		date: `${year}-05-12`,
		name: 'Visak Bochea Day',
		translations: { km: 'ព្រះពុទ្ធជ័យន្ត' },
		variable: true,
	},
	{
		date: `${year}-10-03`,
		name: 'Pchum Ben Day (Day 1)',
		translations: { km: 'ទិវាបុណ្យភ្ជុំបិណ្ឌ (ថ្ងៃទី១)' },
		variable: true,
	},
	{
		date: `${year}-10-04`,
		name: 'Pchum Ben Day (Day 2)',
		translations: { km: 'ទិវាបុណ្យភ្ជុំបិណ្ឌ (ថ្ងៃទី២)' },
		variable: true,
	},
	{
		date: `${year}-10-05`,
		name: 'Pchum Ben Day (Day 3)',
		translations: { km: 'ទិវាបុណ្យភ្ជុំបិណ្ឌ (ថ្ងៃទី៣)' },
		variable: true,
	},
	{
		date: `${year}-11-05`,
		name: 'Water Festival (Day 1)',
		translations: { km: 'បុណ្យអ៊ំទូក (ថ្ងៃទី១)' },
		variable: true,
	},
	{
		date: `${year}-11-06`,
		name: 'Water Festival (Day 2)',
		translations: { km: 'បុណ្យអ៊ំទូក (ថ្ងៃទី២)' },
		variable: true,
	},
	{
		date: `${year}-11-07`,
		name: 'Water Festival (Day 3)',
		translations: { km: 'បុណ្យអ៊ំទូក (ថ្ងៃទី៣)' },
		variable: true,
	},
]

// Lookup map: date string → KH translation, for form auto-fill
const buildKhLookup = (year: number): Record<string, string> =>
	Object.fromEntries(
		getCambodiaHolidays(year)
			.filter((h) => h.translations?.km)
			.map((h) => [h.date, h.translations!.km]),
	)

interface PublicHolidaysProps {
	embedded?: boolean
}

const PublicHolidays: React.FC<PublicHolidaysProps> = ({ embedded = false }) => {
	const { isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary, text, textSecondary, bg, border } = themeColors
	const navigate = useNavigate()

	const [year, setYear] = useState<number>(CURRENT_YEAR)
	const [holidays, setHolidays] = useState<Holiday[]>([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [dirty, setDirty] = useState(false)

	// Add / Edit modal
	const [modalOpen, setModalOpen] = useState(false)
	const [editIndex, setEditIndex] = useState<number | null>(null)
	const [form] = Form.useForm()

	// ── Fetch ──────────────────────────────────────────────────────────────
	const fetchHolidays = useCallback(async (y: number) => {
		setLoading(true)
		setDirty(false)
		try {
			const res = await leaveService.getHolidays(y)
			const list: Holiday[] = res?.data?.holidays ?? []
			// Sort by date ascending
			list.sort((a, b) => a.date.localeCompare(b.date))
			setHolidays(list)
		} catch {
			message.error('Failed to load holidays')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchHolidays(year)
	}, [year, fetchHolidays])

	// ── Apply Cambodia defaults ───────────────────────────────────────────
	const handleApplyCambodiaDefaults = () => {
		const defaults = getCambodiaHolidays(year)
		defaults.sort((a, b) => a.date.localeCompare(b.date))
		setHolidays(defaults)
		setDirty(true)
		message.success(
			`${defaults.length} Cambodia holidays loaded for ${year}. ` +
				'Variable-date holidays (orange) must be confirmed by Royal Decree each year.',
		)
	}

	// ── Save all ───────────────────────────────────────────────────────────
	const handleSave = async () => {
		setSaving(true)
		try {
			await leaveService.setHolidays(year, holidays)
			message.success(`${holidays.length} holidays saved for ${year}`)
			setDirty(false)
		} catch {
			message.error('Failed to save holidays')
		} finally {
			setSaving(false)
		}
	}

	// ── Back navigation ──────────────────────────────────────────────────
	const handleBack = () => {
		if (dirty) {
			Modal.confirm({
				title: 'Unsaved Changes',
				content:
					'You have unsaved changes that will be lost. Do you want to save before leaving?',
				okText: 'Save & Leave',
				cancelText: 'Leave without Saving',
				onOk: async () => {
					await handleSave()
					navigate('/leaves')
				},
				onCancel: () => navigate('/leaves'),
			})
		} else {
			navigate('/leaves')
		}
	}

	// ── Add / Edit modal ───────────────────────────────────────────────────
	const openAdd = () => {
		setEditIndex(null)
		form.resetFields()
		setModalOpen(true)
	}

	const openDuplicate = (index: number) => {
		setEditIndex(null)
		const h = holidays[index]
		form.setFieldsValue({
			date: dayjs(h.date),
			name: h.name,
			translationKm: h.translations?.km ?? '',
			variable: h.variable ?? false,
		})
		setModalOpen(true)
	}

	const openEdit = (index: number) => {
		setEditIndex(index)
		const h = holidays[index]
		form.setFieldsValue({
			date: dayjs(h.date),
			name: h.name,
			translationKm: h.translations?.km ?? '',
			variable: h.variable ?? false,
		})
		setModalOpen(true)
	}

	const handleModalOk = () => {
		form.validateFields().then((values) => {
			const holiday: Holiday = {
				date: (values.date as dayjs.Dayjs).format('YYYY-MM-DD'),
				name: values.name,
				translations: values.translationKm ? { km: values.translationKm } : undefined,
				variable: values.variable ?? false,
			}
			setHolidays((prev) => {
				const updated = [...prev]
				if (editIndex !== null) {
					updated[editIndex] = holiday
				} else {
					updated.push(holiday)
				}
				// Keep sorted
				updated.sort((a, b) => a.date.localeCompare(b.date))
				return updated
			})
			setDirty(true)
			setModalOpen(false)
		})
	}

	const handleDelete = (index: number) => {
		setHolidays((prev) => prev.filter((_, i) => i !== index))
		setDirty(true)
	}

	// ── Table columns ──────────────────────────────────────────────────────
	const columns: ColumnsType<Holiday> = [
		{
			title: '#',
			width: 50,
			render: (_v, _r, i) => i + 1,
		},
		{
			title: 'Date',
			dataIndex: 'date',
			width: 120,
			render: (v: string) => (
				<Typography.Text>{dayjs(v).format('DD MMM YYYY')}</Typography.Text>
			),
		},
		{
			title: 'Day',
			dataIndex: 'date',
			width: 90,
			render: (v: string) => (
				<Typography.Text type='secondary'>{dayjs(v).format('ddd')}</Typography.Text>
			),
		},
		{
			title: 'Name (EN)',
			dataIndex: 'name',
		},
		{
			title: 'Name (KH)',
			render: (_v: unknown, r: Holiday) => r.translations?.km || '—',
		},
		{
			title: 'Type',
			dataIndex: 'variable',
			width: 110,
			render: (v?: boolean) =>
				v ? (
					<Tooltip title='Lunar / variable date — confirm annually by Royal Decree'>
						<Tag color='orange'>Variable</Tag>
					</Tooltip>
				) : (
					<Tag color='blue'>Fixed</Tag>
				),
		},
		{
			title: 'Actions',
			width: 130,
			render: (_v, _r, i) => (
				<Space>
					<Tooltip title='Edit'>
						<Button
							type='text'
							icon={<EditOutlined />}
							size='small'
							onClick={() => openEdit(i)}
						/>
					</Tooltip>
					<Tooltip title='Duplicate'>
						<Button
							type='text'
							icon={<CopyOutlined />}
							size='small'
							onClick={() => openDuplicate(i)}
						/>
					</Tooltip>
					<Popconfirm
						title='Remove this holiday?'
						onConfirm={() => handleDelete(i)}
						okText='Remove'
						okButtonProps={{ danger: true }}
					>
						<Button danger type='text' icon={<DeleteOutlined />} size='small' />
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<>
			{!embedded && <GlobalHeader />}

			{/* ── Page Header ─────────────────────────────────────────────── */}
			<div
				style={{
					background: bg,
					borderBottom: `1px solid ${border}`,
					padding: '14px 24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: 10,
							background: isDarkMode ? `${primary}15` : primary,
							color: isDarkMode ? primary : '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<CalendarOutlined style={{ fontSize: 24 }} />
					</div>
					<div>
						<div
							style={{
								fontSize: 20,
								fontWeight: 700,
								color: text,
								lineHeight: '1.2',
							}}
						>
							Public Holidays
						</div>
						<div
							style={{
								fontSize: 12,
								color: textSecondary,
								marginTop: 2,
								display: 'flex',
								alignItems: 'center',
								gap: 8,
							}}
						>
							<Select
								value={year}
								onChange={(v) => setYear(v)}
								options={YEARS.map((y) => ({ value: y, label: String(y) }))}
								style={{ width: 100 }}
								size='small'
							/>
							<span>
								{holidays.length} holiday{holidays.length !== 1 ? 's' : ''}{' '}
								configured
							</span>
						</div>
					</div>
				</div>
				<Space size={8}>
					<Popconfirm
						title={`Load Cambodia default holidays for ${year}?`}
						description={
							<span style={{ maxWidth: 320, display: 'block' }}>
								This will <strong>replace</strong> the current list with 22 official
								Cambodia public holidays. Variable-date holidays (lunar) are
								approximate and must be confirmed annually.
							</span>
						}
						onConfirm={handleApplyCambodiaDefaults}
						okText='Load Defaults'
						cancelText='Cancel'
					>
						<Button icon={<GlobalOutlined />}>Use Cambodia Defaults</Button>
					</Popconfirm>
					<Button icon={<PlusOutlined />} onClick={openAdd}>
						Add Holiday
					</Button>
					<Button type='primary' loading={saving} disabled={!dirty} onClick={handleSave}>
						Save Changes
					</Button>
					{!embedded ? <Button onClick={handleBack}>← Back to Leaves</Button> : null}
				</Space>
			</div>

			{dirty && (
				<div style={{ padding: '8px 24px 0' }}>
					<Card
						size='small'
						style={{ borderRadius: 8, background: '#fffbe6', borderColor: '#ffe58f' }}
					>
						<Typography.Text type='warning'>
							You have unsaved changes. Click <strong>Save Changes</strong> to persist
							them.
						</Typography.Text>
					</Card>
				</div>
			)}

			{/* ── Table ───────────────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					<Table<Holiday>
						columns={columns}
						dataSource={holidays}
						rowKey={(h) => h.date + h.name}
						loading={loading}
						pagination={{ pageSize: 30, hideOnSinglePage: true }}
						size='small'
						scroll={{ x: 700 }}
					/>
				</Card>
			</div>

			{/* Add / Edit Modal */}
			<Modal
				open={modalOpen}
				title={editIndex !== null ? 'Edit Holiday' : 'Add Holiday'}
				onCancel={() => setModalOpen(false)}
				onOk={handleModalOk}
				okText='Save'
				destroyOnClose
			>
				<Form
					form={form}
					layout='vertical'
					onValuesChange={(changed) => {
						if (changed.date) {
							const dateStr = (changed.date as dayjs.Dayjs).format('YYYY-MM-DD')
							const lookup = buildKhLookup(year)
							if (lookup[dateStr] && !form.getFieldValue('translationKm')) {
								form.setFieldValue('translationKm', lookup[dateStr])
							}
						}
					}}
				>
					<Form.Item
						label='Date'
						name='date'
						rules={[{ required: true, message: 'Please select a date' }]}
					>
						<DatePicker
							style={{ width: '100%' }}
							format='DD MMM YYYY'
							disabledDate={(d) => d.year() !== year}
						/>
					</Form.Item>
					<Form.Item
						label='Name (English)'
						name='name'
						rules={[{ required: true, message: 'Please enter a name' }]}
					>
						<Input placeholder='e.g. Khmer New Year (Day 1)' />
					</Form.Item>
					<Form.Item label='Name (Khmer)' name='translationKm'>
						<Input placeholder='e.g. ចូលឆ្នាំខ្មែរ (ថ្ងៃទី១)' />
					</Form.Item>
					<Form.Item
						label='Variable (lunar / confirmed by Royal Decree annually)'
						name='variable'
						valuePropName='checked'
					>
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</>
	)
}

export default PublicHolidays
